import api from "../services/authService";

/**
 * Attorney Inbox API client.
 *
 * Wraps the inbox endpoints exposed by the `documents` Django app.
 * Backend lives at `dms_backend/documents/views/inbox/` (slice 1 PR #218,
 * slice 2 PR #223 merged 2b2e2e7, unread-count semantics PR #226 merged
 * 44d8f21c). All calls flow through the shared `authService` axios
 * instance for JWT + CSRF + 401-refresh.
 *
 * Surface summary:
 *   Reads (Phase 5a)
 *     - list inbox items (paginated)         → listInboxItems
 *     - unread badge count                   → getInboxUnreadCount
 *     - list tasks for a document            → listInboxTasks
 *     - list messages for a document         → listInboxMessages
 *   Writes (Phase 5b)
 *     - mark-reviewed / mark-unread          → markInboxReviewed/Unread
 *     - snooze / unsnooze                    → snoozeInboxItem / unsnoozeInboxItem
 *     - create task / update task status     → createInboxTask / updateInboxTaskStatus
 *     - create message / mark-read           → createInboxMessage / markInboxMessageRead
 *
 * Conventions match service/analysis.js:
 *   - Functions return the raw axios response so callers can inspect
 *     status codes when useful.
 *   - DRF PageNumberPagination envelopes are returned verbatim
 *     (`{ count, next, previous, results }`); the slice unwraps them.
 *   - Document IDs are integers (BE handoff §1 — `<int:document_id>`).
 *     Callers don't need to cast, but please don't pass UUIDs.
 */

/**
 * GET /api/inbox/items/?matter_id=<int>&include_snoozed=false&page=1&page_size=20
 *
 * Returns a DRF-paginated list of inbox items for the requesting user.
 * Sorted newest-first by `received_at` (the backend's alias for
 * `Document.uploaded_at`).
 *
 * Defaults match the BE contract: page_size 20, max 100, snoozed hidden.
 * Pass `matter_id` to scope to a single matter (UI matter-filter chip);
 * pass `include_snoozed: true` to include snoozed rows alongside
 * non-snoozed (with `snoozed_until` populated on those rows).
 *
 * `recentlyReviewed: true` returns the reviewed pile instead — the past
 * two weeks of mail this user marked reviewed, most recently reviewed
 * first, each row carrying `reviewed_at`.
 */
export const listInboxItems = ({
  matterId,
  includeSnoozed = false,
  recentlyReviewed = false,
  category = null,
  page = 1,
  pageSize = 20,
} = {}) => {
  const params = new URLSearchParams();
  if (matterId != null) params.set("matter_id", String(matterId));
  if (includeSnoozed) params.set("include_snoozed", "true");
  // Two piles, never mixed. Normally reviewed docs leave the inbox
  // (Matthew, 2026-07-17) and the backend default is
  // include_reviewed=true for older clients, so the hide is expressed
  // explicitly. `reviewed_only` asks for the other pile instead: the
  // last fortnight of this user's reviewed mail, newest review first.
  if (recentlyReviewed) params.set("reviewed_only", "true");
  else params.set("include_reviewed", "false");
  if (category) params.set("category", String(category));
  if (page && page !== 1) params.set("page", String(page));
  if (pageSize && pageSize !== 20) params.set("page_size", String(pageSize));
  const qs = params.toString();
  const url = qs ? `/inbox/items/?${qs}` : `/inbox/items/`;
  return api.get(url);
};

/**
 * GET /api/inbox/unread-count/?include_snoozed=false
 *
 * Returns `{ unread_count: <int> }`. Drives the sidebar badge.
 *
 * Default semantics (Gmail/Slack-style — what the sidebar wants):
 *   - currently-snoozed items are EXCLUDED from the count.
 *   - elapsed snoozes (whose `until` is already past) are INCLUDED;
 *     the row resurfaces in the list and counts toward unread, matching
 *     the list view's behavior.
 *   - the `include_snoozed=true` opt-in flips to "all unread regardless
 *     of snooze". The sidebar never needs that variant; it's exposed
 *     here for an eventual "all unread" overview surface.
 *
 * Locked behind BE PR #226 (44d8f21c) — the earlier handoff doc note
 * that "snoozed items still count" no longer applies.
 */
export const getInboxUnreadCount = ({ includeSnoozed = false } = {}) => {
  const url = includeSnoozed
    ? `/inbox/unread-count/?include_snoozed=true`
    : `/inbox/unread-count/`;
  return api.get(url);
};

/**
 * GET /api/inbox/items/<int:document_id>/tasks/
 *
 * Returns `InboxTask[]` for a document — newest-first by created_at.
 * Lazy-fetched: only called when the right-rail Tasks pane (Phase 5b)
 * is opened. Phase 5a doesn't surface this in the UI yet, but the
 * service hook is here so the slice can grow into it without an
 * additional service-layer change.
 */
export const listInboxTasks = (documentId) => {
  if (documentId == null) return Promise.resolve(null);
  return api.get(`/inbox/items/${documentId}/tasks/`);
};

/**
 * GET /api/inbox/items/<int:document_id>/messages/
 *
 * Returns `InboxMessage[]` for a document — newest-first by created_at.
 * Same lazy-fetch contract as tasks.
 */
export const listInboxMessages = (documentId) => {
  if (documentId == null) return Promise.resolve(null);
  return api.get(`/inbox/items/${documentId}/messages/`);
};

// ---------------------------------------------------------------------------
// Write surface (Phase 5b)
// ---------------------------------------------------------------------------

/**
 * POST /api/inbox/items/<int:document_id>/mark-reviewed/
 *
 * Idempotent — upserts an `InboxReadState(user=<request user>, document=<id>,
 * read_at=now())`. Returns `{ is_read: true, read_at: <ISO> }`. Safe to call
 * even if the row already exists (server is the source of truth on `read_at`,
 * so a repeat call just refreshes the timestamp; the FE shouldn't depend on
 * that, but it won't 4xx).
 *
 * Inbox delegation: when the reader covers the attorney whose letter this
 * is, the backend also marks it reviewed for that attorney. Pass
 * `{ propagate: false }` ("Keep in Chris's mailbox") to mark it for the
 * reader alone. Omitted means the backend default — reviewed for both.
 */
export const markInboxReviewed = (documentId, { propagate } = {}) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  const body = propagate === false ? { propagate: false } : {};
  return api.post(`/inbox/items/${documentId}/mark-reviewed/`, body);
};

/**
 * POST /api/inbox/open-timings/
 *
 * The viewer's click-to-page-drawn stopwatch report (see
 * `pages/Inbox/inboxOpenStopwatch.js`). Body:
 * `{ document_id, outcome, total_ms, steps, context }`; returns 204.
 */
export const postInboxOpenTiming = (payload) =>
  api.post("/inbox/open-timings/", payload);

/**
 * POST /api/inbox/items/<int:document_id>/mark-unread/
 *
 * Inverse of mark-reviewed — deletes the user's `InboxReadState` row. Also
 * idempotent: removing a non-existent row is a no-op (200), not a 404. Returns
 * `{ is_read: false, read_at: null }`.
 */
export const markInboxUnread = (documentId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.post(`/inbox/items/${documentId}/mark-unread/`, {});
};

/**
 * POST /api/inbox/items/<int:document_id>/snooze/
 *
 * Body shape (per BE handoff §2.4) — pick exactly one:
 *   { until: "<ISO timestamp in the future>" }   absolute snooze
 *   { duration_days: <number > 0> }              relative-from-now sugar
 *
 * The FE preset menu (4h / 24h / 3d / 1w) goes through the absolute-`until`
 * variant since we want a precise rest-on-the-wall-clock contract; the custom
 * picker also writes `until`. `duration_days` is exposed for completeness in
 * case a future surface (e.g. a CLI-style "/snooze 3d" command) ever wants
 * shorthand.
 *
 * Returns `{ snoozed_until: "<ISO>" }`. BE rejects non-future timestamps
 * with 400; the FE validates client-side first to keep the UI snappy.
 */
export const snoozeInboxItem = (documentId, payload) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  if (!payload || (!payload.until && payload.duration_days == null)) {
    return Promise.reject(new Error("snooze payload requires { until } or { duration_days }"));
  }
  return api.post(`/inbox/items/${documentId}/snooze/`, payload);
};

/**
 * POST /api/inbox/items/<int:document_id>/unsnooze/
 *
 * Deletes the active `InboxSnooze` row for (user, document). Idempotent.
 * Returns `{ snoozed_until: null }`.
 */
export const unsnoozeInboxItem = (documentId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.post(`/inbox/items/${documentId}/unsnooze/`, {});
};

/**
 * POST /api/inbox/items/<document_id>/duplicate/dismiss/
 *
 * "This is not a copy of that." Clears the duplicate flag from the row.
 *
 * `matchId` is optional — omitted, the BE dismisses the row's best active
 * match, which is the one the UI is showing. Pass it when the caller knows
 * exactly which match it rendered, so a flag recomputed between render and
 * click can't be dismissed by mistake.
 *
 * Dismissal is recorded on the match, not per user: whether two documents
 * are the same letter is a fact about the documents, so the whole team sees
 * the result. Idempotent. Returns `{ match_id, dismissed_at, duplicate_flag: null }`.
 */
export const dismissInboxDuplicate = (documentId, matchId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.post(
    `/inbox/items/${documentId}/duplicate/dismiss/`,
    matchId == null ? {} : { match_id: matchId }
  );
};

/**
 * POST /api/inbox/items/<document_id>/duplicate/restore/
 *
 * Undo a dismissal. Exists because dismissal is shared across the team,
 * which would otherwise make one misclick permanent for everyone.
 * Returns `{ match_id, duplicate_flag }` with the flag restored.
 */
export const restoreInboxDuplicate = (documentId, matchId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.post(
    `/inbox/items/${documentId}/duplicate/restore/`,
    matchId == null ? {} : { match_id: matchId }
  );
};

/**
 * Create a Task. With a source file this still posts to the document
 * path so existing callers keep working; without one it posts to
 * `/api/tasks/` (Matthew, 2026-08-18).
 *
 * Body: `{ assigned_to_user_id, body, kind?, due_date?, priority?,
 * matter_id? }`. `kind` is accepted from older clients and ignored by
 * the server — new work is always a Task. `due_date` is available on
 * every Task. `priority` is `"urgent"` or omitted.
 */
export const createInboxTask = (
  documentId,
  { assigned_to_user_id, body, kind, due_date, priority, matter_id } = {}
) => {
  if (assigned_to_user_id == null) {
    return Promise.reject(new Error("assigned_to_user_id required"));
  }
  if (!body || !String(body).trim()) {
    return Promise.reject(new Error("body required"));
  }
  const payload = {
    assigned_to_user_id,
    body,
    ...(kind ? { kind } : {}),
    ...(due_date ? { due_date } : {}),
    ...(priority ? { priority } : {}),
    ...(matter_id ? { matter_id } : {}),
  };
  if (documentId == null || documentId === "") {
    return api.post(`/tasks/`, payload);
  }
  return api.post(`/inbox/items/${documentId}/tasks/`, payload);
};

/**
 * PATCH /api/inbox/tasks/<int:task_id>/
 *
 * Updates the task `status`. Setting `status="completed"` triggers BE-side
 * `completed_at = now()` so the FE doesn't have to. Authorization is
 * BE-enforced — `assigned_by` and `assigned_to` may PATCH; everyone else
 * gets 403.
 */
export const updateInboxTaskStatus = (taskId, status) => {
  if (taskId == null) return Promise.reject(new Error("taskId required"));
  if (!status) return Promise.reject(new Error("status required"));
  return api.patch(`/inbox/tasks/${taskId}/`, { status });
};

/**
 * POST /api/inbox/items/<int:document_id>/messages/
 *
 * Creates a new `InboxMessage` and a side-effect Notification for the
 * recipient. Body: `{ recipient_user_id, body, cc_user_ids? }`. Cc'd
 * users become full participants on the underlying thread — they see
 * every future reply and can reply themselves. Returns the serialized
 * message. Same client-side body-validation contract as tasks.
 */
export const createInboxMessage = (
  documentId,
  { recipient_user_id, body, cc_user_ids } = {}
) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  if (recipient_user_id == null) {
    return Promise.reject(new Error("recipient_user_id required"));
  }
  if (!body || !String(body).trim()) {
    return Promise.reject(new Error("body required"));
  }
  return api.post(`/inbox/items/${documentId}/messages/`, {
    recipient_user_id,
    body,
    ...(cc_user_ids?.length ? { cc_user_ids } : {}),
  });
};

/**
 * POST /api/inbox/messages/<int:message_id>/mark-read/
 *
 * Marks the message read on behalf of the recipient. BE 403s if the
 * caller isn't the recipient. Returns the serialized message with
 * `read_at` populated.
 */
export const markInboxMessageRead = (messageId) => {
  if (messageId == null) return Promise.reject(new Error("messageId required"));
  return api.post(`/inbox/messages/${messageId}/mark-read/`, {});
};

export const getInboxMessageThread = (threadId) =>
  api.get(`/inbox/messages/threads/${threadId}/`);

export const listInboxMessageRecipients = (search = "") =>
  api.get(`/inbox/messages/recipients/?search=${encodeURIComponent(search)}`);

export const createInboxMessageThread = ({
  matter_id,
  document_id,
  recipient_user_id,
  body,
  cc_user_ids,
}) => {
  if (!matter_id || recipient_user_id == null || !body?.trim()) {
    return Promise.reject(new Error("Matter, recipient, and message are required"));
  }
  return api.post("/inbox/messages/threads/", {
    matter_id,
    ...(document_id ? { document_id } : {}),
    recipient_user_id,
    body: body.trim(),
    ...(cc_user_ids?.length ? { cc_user_ids } : {}),
  });
};

/**
 * POST /api/inbox/messages/threads/<int:thread_id>/reply/
 *
 * `add_participant_ids` loops people into the conversation mid-thread:
 * they're added as full participants before this reply is delivered, so
 * they receive it and see the whole prior history.
 */
export const replyToInboxMessageThread = (threadId, body, add_participant_ids) => {
  if (!body || !String(body).trim()) {
    return Promise.reject(new Error("message body required"));
  }
  return api.post(`/inbox/messages/threads/${threadId}/reply/`, {
    body,
    ...(add_participant_ids?.length ? { add_participant_ids } : {}),
  });
};

export const markInboxMessageThreadRead = (threadId) =>
  api.post(`/inbox/messages/threads/${threadId}/mark-read/`, {});

// ---------------------------------------------------------------------------
// Work items (new Salesforce org — BE dms_backend #2013)
// ---------------------------------------------------------------------------

/**
 * GET /api/inbox/items/<document_id>/work-item-context/
 *
 * Everything the Create Work Item form needs before it renders:
 * `{ enabled, matter_found, matter_name, client_name,
 *    default_recipient_id, default_recipient_name, recipients }`.
 * Recipients are NEW-org users/queues (`{ id, name, role }`), matter
 * team first with the case manager as the default — not DMS users.
 * `matter_found: false` means the matter hasn't migrated to the new
 * Salesforce yet; the modal explains and points at Create Task.
 */
export const getWorkItemContext = (documentId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.get(`/inbox/items/${documentId}/work-item-context/`);
};

/**
 * POST /api/inbox/items/<document_id>/work-items/
 *
 * Creates a Work_Item__c in the NEW Salesforce org — the
 * attorney→paralegal baton — exactly as if composed in the claim
 * tracker: the caller (by email) is the requester, the chosen new-org
 * assignee gets it in their court with one bell notification, and a
 * deep link back to this document rides with the instruction.
 *
 * Body: `{ assignee_id, instruction, type?, subject?, due_date?,
 * condition_ids?, chain? }` where `assignee_id` is a NEW-org user/queue
 * id from the context call and `instruction` is HTML. A plan sends
 * `chain` instead of a single instruction. `due_date` is `yyyy-mm-dd`.
 * Returns `{ work_item_id, work_item_number }`; errors come back with an
 * honest `detail` (unmigrated matter, no matching new-org user for the
 * caller's email, org unreachable).
 *
 * Every field the composer sets has to be forwarded. Dropping `due_date`
 * here made a date picked on a document look saved while the server
 * never received it.
 */
export const createWorkItem = (
  documentId,
  {
    assignee_id,
    instruction,
    type,
    subject,
    due_date,
    condition_ids,
    chain,
  } = {}
) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  if (assignee_id == null) {
    return Promise.reject(new Error("assignee_id required"));
  }
  const steps = Array.isArray(chain) ? chain : null;
  const hasChain = steps != null && steps.length > 0;
  if (!hasChain && (!instruction || !String(instruction).trim())) {
    return Promise.reject(new Error("instruction required"));
  }
  const pins = Array.isArray(condition_ids)
    ? condition_ids.filter((id) => id != null && String(id).trim())
    : [];
  return api.post(`/inbox/items/${documentId}/work-items/`, {
    assignee_id,
    ...(hasChain
      ? { chain: steps }
      : {
          instruction,
          ...(type ? { type } : {}),
          ...(subject ? { subject } : {}),
        }),
    ...(due_date ? { due_date } : {}),
    ...(pins.length ? { condition_ids: pins } : {}),
  });
};

/**
 * GET /api/inbox/items/<document_id>/waiting-on/
 *
 * What this document's matter is waiting to receive — the open (Sent)
 * work items: `{ enabled, matter_found, items: [{ workItemId,
 * itemNumber, subject, typeValue, holderName, requestedByName, ageDays,
 * staleness, dueDate }] }`. Drives the inbox banner ("Waiting on:
 * client statement — sent to Anne, 9d") and the attach picker. Quietly
 * empty when the feature is off or the matter hasn't migrated.
 */
export const getWaitingOnWorkItems = (documentId) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  return api.get(`/inbox/items/${documentId}/waiting-on/`);
};

/**
 * POST /api/inbox/items/<document_id>/attach-to-work-item/
 *
 * The conveyor belt: attach this document to the open work item it
 * answers. The document link lands in the item's thread and a Sent
 * item volleys back to the requester's court with one bell
 * (`volleyedToRequester: true` in the response). Body:
 * `{ work_item_id, note? }` — note is plain HTML like the composer.
 */
export const attachDocumentToWorkItem = (
  documentId,
  { work_item_id, note } = {}
) => {
  if (documentId == null) return Promise.reject(new Error("documentId required"));
  if (!work_item_id) {
    return Promise.reject(new Error("work_item_id required"));
  }
  return api.post(`/inbox/items/${documentId}/attach-to-work-item/`, {
    work_item_id,
    ...(note ? { note } : {}),
  });
};

// ---------------------------------------------------------------------------
// Deadline cards (deadline calendaring — BE dms_backend #1325)
// ---------------------------------------------------------------------------

/**
 * GET /api/inbox/deadline-cards/?status=proposed&matter_id=<id>
 *
 * Returns `{ results: DeadlineCard[] }` (capped at 100, newest first) —
 * cards on matters the user can see PLUS cards addressed directly to
 * them (the assigner-fallback recipient). Default status filter is
 * `proposed` (the actionable set); pass `status: "all"` for audit
 * views.
 */
export const listDeadlineCards = ({ status = "proposed", matterId } = {}) => {
  const params = new URLSearchParams();
  if (status && status !== "proposed") params.set("status", status);
  if (matterId != null) params.set("matter_id", String(matterId));
  const qs = params.toString();
  return api.get(qs ? `/inbox/deadline-cards/?${qs}` : `/inbox/deadline-cards/`);
};

/**
 * POST /api/inbox/deadline-cards/<int:card_id>/confirm/
 *
 * One-click [Create event]. Body is optional `{ deadline_date:
 * "YYYY-MM-DD" }` — REQUIRED when the card has no computed deadline
 * (the system never guesses a statutory deadline; the human supplies
 * it), and an override when it does. Returns the confirmed card;
 * the Salesforce Event is created asynchronously after commit.
 */
export const confirmDeadlineCard = (cardId, { deadlineDate } = {}) => {
  if (cardId == null) return Promise.reject(new Error("cardId required"));
  const body = deadlineDate ? { deadline_date: deadlineDate } : {};
  return api.post(`/inbox/deadline-cards/${cardId}/confirm/`, body);
};

/**
 * POST /api/inbox/deadline-cards/<int:card_id>/dismiss/
 *
 * Body: `{ reason, note }`. Reason is mandatory (a deadline can be
 * refused, never silently ignored) and `reason: "other"` requires the
 * note — both validated client-side too so the modal shows inline
 * errors instead of a server round-trip.
 */
export const dismissDeadlineCard = (cardId, { reason, note = "" } = {}) => {
  if (cardId == null) return Promise.reject(new Error("cardId required"));
  if (!reason) return Promise.reject(new Error("reason required"));
  if (reason === "other" && !String(note).trim()) {
    return Promise.reject(new Error("reason 'other' requires a note"));
  }
  return api.post(`/inbox/deadline-cards/${cardId}/dismiss/`, { reason, note });
};
